import React from "react";
import styled from "styled-components";
import { useImageZoom } from "../hooks/useImageZoom";
import { ZoomControls } from "./ZoomControls";

/**
 * 単体画像の拡縮 / ドラッグ移動 / ホイール拡縮 / キーボード操作 をまとめた
 * 再利用可能 Lightbox コンポーネント。
 *
 * - リグレッション 1 回目実行の Current 画像（issue #195）
 * - EvidenceViewer の通常エビデンス画像
 *
 * tfact_user_request#218: 画像クリック / ダブルクリックでの拡大は廃止し、
 * 拡大はホイール / 下部ボタン / キーボード (+/-/0) に統一する。
 *
 * モードタブ等の余計な UI を含まない最小構成。複数モードを切り替える
 * 用途では ImageCompareModal を使う。
 */

const Overlay = styled.div<{ isOpen: boolean }>`
  display: ${(p) => (p.isOpen ? "flex" : "none")};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  justify-content: center;
  align-items: center;
  z-index: 1000;
  cursor: pointer;
`;

const Content = styled.div`
  position: relative;
  max-width: 95%;
  max-height: 95%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Image = styled.img<{
  scale: number;
  position: { x: number; y: number };
  isDragging: boolean;
}>`
  max-width: none;
  max-height: none;
  width: auto;
  height: auto;
  max-width: 95vw;
  max-height: 95vh;
  object-fit: contain;
  border-radius: 8px;
  transform: scale(${(p) => p.scale}) translate(${(p) => p.position.x}px, ${(p) => p.position.y}px);
  transition: ${(p) => (p.isDragging ? "none" : "transform 0.2s")};
  cursor: ${(p) => (p.isDragging ? "grabbing" : "grab")};
  user-select: none;
  -webkit-user-drag: none;
`;

export interface ZoomableImageModalProps {
  isOpen: boolean;
  imageUrl: string | null;
  onClose: () => void;
  alt?: string;
}

export const ZoomableImageModal: React.FC<ZoomableImageModalProps> = ({
  isOpen,
  imageUrl,
  onClose,
  alt = "拡大画像",
}) => {
  const enabled = isOpen && !!imageUrl;
  const zoom = useImageZoom({ enabled, onEscape: onClose });

  if (!enabled) return null;

  return (
    <Overlay
      data-testid="zoomable-image-overlay"
      isOpen={isOpen}
      onClick={onClose}
    >
      <Content
        data-testid="zoomable-image-content"
        onClick={(e) => e.stopPropagation()}
      >
        <Image
          src={imageUrl!}
          alt={alt}
          scale={zoom.scale}
          position={zoom.position}
          isDragging={zoom.isDragging}
          onMouseDown={zoom.handleMouseDown}
        />
      </Content>
      <ZoomControls
        scale={zoom.scale}
        position={zoom.position}
        onZoomIn={zoom.zoomIn}
        onZoomOut={zoom.zoomOut}
        onResetZoom={zoom.resetZoom}
        onResetPosition={zoom.resetPosition}
      />
    </Overlay>
  );
};
